(() => {
  'use strict';
  function collectionKey(value, origin) {
    const url = new URL(value, origin);
    if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+(?:\/[^/]+)?\/?$/i.test(url.pathname)) return null;
    const params = new URLSearchParams();
    for (const [key, val] of url.searchParams) {
      if (val !== '' && (key === 'sort_by' || key === 'page' || key.startsWith('filter.'))) params.append(key, val);
    }
    params.sort();
    return url.pathname + (params.size ? '?' + params.toString() : '');
  }
  function safePage(value, origin, baseKey) {
    try {
      const url = new URL(value, origin);
      const base = new URL(baseKey, origin);
      const page = Number(url.searchParams.get('page') || 1);
      if (url.origin !== origin || url.pathname !== base.pathname || !Number.isInteger(page) || page < 1) return null;
      url.searchParams.delete('section_id');
      const compare = new URL(url); compare.searchParams.delete('page');
      base.searchParams.delete('page');
      if (collectionKey(compare, origin) !== collectionKey(base, origin)) return null;
      return url.pathname + url.search;
    } catch { return null; }
  }
  function validSaved(saved, key, now = Date.now()) {
    return Boolean(saved && saved.key === key && now - saved.time >= 0 && now - saved.time < 1800000 && Array.isArray(saved.pages) && saved.pages.length <= 60 && saved.pages.every(p => typeof p === 'string') && Number.isFinite(saved.scroll) && saved.scroll >= 0);
  }
  function shouldRestore(saved, key, historyMarker, now = Date.now()) {
    // A marked history entry remains the same browsing visit after BFCache
    // and native facet rerenders, even when pageshow consumed pending.
    return validSaved(saved, key, now) && (saved.pending === true || historyMarker === key);
  }
  function returnHistoryState(state, key, search, saved) {
    // Dawn reads searchParams on popstate. A custom non-null history state
    // without that property makes it rerender an unrelated first page.
    return { ...state, searchParams: search.replace(/^\?/, ''), owwLoadMoreReturn: key, owwLoadMoreSnapshot: saved };
  }
  function restoreRecord(snapshot, stored, key, now = Date.now()) {
    // Different collection visits must not overwrite each other's back state.
    return validSaved(snapshot, key, now) ? snapshot : stored;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { collectionKey, safePage, validSaved, shouldRestore, returnHistoryState, restoreRecord };
  if (typeof document === 'undefined' || customElements.get('oww-load-more')) return;
  class LoadMore extends HTMLElement {
    connectedCallback() {
      // Dawn renders cached facet HTML synchronously, then pushes the new URL.
      // Wait until that call stack completes before deriving the state key.
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }
    initialize() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.closest('#ProductGridContainer')?.querySelector('ul#product-grid');
      this.button = this.querySelector('[data-load-more-button]');
      this.counter = this.querySelector('[data-load-more-count]');
      this.status = this.querySelector('[data-load-more-status]');
      this.fallback = this.querySelector('[data-load-more-fallback]');
      if (!this.grid || !this.button || !this.counter || !this.fallback) return;
      this.key = collectionKey(location.href, location.origin);
      this.storageKey = 'oww-load-more:' + this.dataset.theme;
      try { this.pages = JSON.parse(this.dataset.loadedPages || '[]'); } catch { this.pages = []; }
      this.next = this.dataset.next || '';
      this.total = Number(this.dataset.total);
      this.controller = new AbortController();
      this.button.hidden = !this.next;
      this.counter.hidden = false;
      this.fallback.hidden = true;
      this.button.addEventListener('click', () => this.load());
      this.remember = event => {
        if (!this.isConnected || this.busy) return;
        const link = event?.target?.closest?.('a[href]');
        if (event?.type === 'click' && (!link || !/\/(products|cart)(\/|\?|$)/.test(link.getAttribute('href') || ''))) return;
        const card = link?.closest('#product-grid > li');
        if (card) this.returnAnchor = { href: card.querySelector('h3 a[href]')?.getAttribute('href'), top: card.getBoundingClientRect().top };
        const saved = { key: this.key, pages: [...this.pages], scroll: window.scrollY, anchor: this.returnAnchor, time: Date.now(), pending: event?.type === 'click' };
        try { sessionStorage.setItem(this.storageKey, JSON.stringify(saved)); } catch { /* Storage is optional. */ }
        if (event?.type === 'click') history.replaceState(returnHistoryState(history.state, this.key, location.search, saved), '', location.href);
      };
      document.addEventListener('click', this.remember, true);
      this.onPageHide = () => {
        // Keep the click intent while updating the final scroll position.
        let pending = false;
        try { pending = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null')?.pending === true; } catch {}
        this.remember();
        if (pending) { try { const r = JSON.parse(sessionStorage.getItem(this.storageKey)); if (r) { r.pending = true; sessionStorage.setItem(this.storageKey, JSON.stringify(r)); } } catch {} }
      };
      window.addEventListener('pagehide', this.onPageHide);
      this.onPageShow = event => {
        if (!event.persisted) return;
        // Refresh UI references as well as the count after history restoration.
        this.button = this.querySelector('[data-load-more-button]');
        this.counter = this.querySelector('[data-load-more-count]');
        this.update();
        // BFCache already retains all cards and scroll. Consume the return
        // marker so a later ordinary menu visit does not restore stale state.
        try {
          const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
          if (saved?.key === this.key) sessionStorage.setItem(this.storageKey, JSON.stringify({ ...saved, pending: false }));
        } catch {}
      };
      window.addEventListener('pageshow', this.onPageShow);
      this.update();
      this.restore();
    }
    disconnectedCallback() {
      this.controller?.abort();
      document.removeEventListener('click', this.remember, true);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
    }
    update() {
      this.counter.textContent = `${this.grid.children.length} van ${this.total} artikelen getoond`;
      this.button.hidden = !this.next;
      this.button.disabled = Boolean(this.busy);
      this.button.textContent = this.busy ? 'Artikelen laden…' : 'Meer artikelen tonen';
    }
    async getPage(value) {
      const page = safePage(value, location.origin, this.key);
      if (!page) throw new Error('Invalid next collection page');
      const url = new URL(page, location.origin);
      // Fetch just the existing Shopify section, keeping filters and sorting.
      url.searchParams.set('section_id', this.dataset.section);
      const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection page failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = html.querySelector('ul#product-grid');
      const control = html.querySelector('oww-load-more');
      if (!grid || !control || !grid.children.length) throw new Error('Missing collection content');
      return { page, grid, control };
    }
    append(result) {
      const cards = Array.from(result.grid.children);
      cards.forEach(card => {
        // Reveal observers only watched the original document. New cards must
        // not remain hidden until a third-party observer notices them.
        card.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
        card.removeAttribute('data-cascade');
        card.querySelectorAll('.scroll-trigger').forEach(node => node.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen'));
        this.grid.appendChild(card);
      });
      this.next = result.control.dataset.next || '';
      this.pages.push(result.page);
      // DOM and runtime describe the same accumulated list on history resume.
      this.dataset.next = this.next;
      this.dataset.loadedPages = JSON.stringify(this.pages);
      // Native pagination always describes the last successfully loaded page.
      const fallback = result.control.querySelector('[data-load-more-fallback]');
      if (fallback) this.fallback.innerHTML = fallback.innerHTML;
      document.dispatchEvent(new CustomEvent('oww:products-appended', { detail: { grid: this.grid, count: cards.length } }));
      return cards;
    }
    async load() {
      if (this.busy || !this.next) return;
      const oldY = window.scrollY;
      const keyboard = this.button.matches(':focus-visible');
      this.busy = true; this.status.textContent = ''; this.update();
      try {
        const cards = this.append(await this.getPage(this.next));
        if (!this.isConnected) return;
        this.status.textContent = `${cards.length} artikelen toegevoegd.`;
        if (keyboard) cards[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
        window.scrollTo({ top: oldY, behavior: 'instant' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Laden lukt nu niet. Probeer het opnieuw of gebruik de paginanummers hieronder.';
          this.fallback.hidden = false;
        }
      } finally { this.busy = false; this.update(); }
    }
    async restore() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { return; }
      saved = restoreRecord(history.state?.owwLoadMoreSnapshot, saved, this.key);
      if (!shouldRestore(saved, this.key, history.state?.owwLoadMoreReturn)) return;
      // Claim this return once; a later ordinary menu visit starts at the top.
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ ...saved, pending: false })); } catch {}
      if (!saved.pages.length) {
        window.scrollTo({ top: saved.scroll, behavior: 'instant' });
        return;
      }
      this.busy = true; this.status.textContent = 'Uw vorige plek wordt teruggezet…'; this.update();
      const previousRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      try {
        // Small bounded batches restore prior browsing without fetching the
        // entire collection or storing product HTML in the browser.
        for (let offset = 0; offset < saved.pages.length; offset += 4) {
          const results = await Promise.all(saved.pages.slice(offset, offset + 4).filter(page => !this.pages.includes(page)).map(page => this.getPage(page)));
          if (!this.isConnected) return;
          results.forEach(result => this.append(result));
        }
        this.status.textContent = 'Uw vorige plek is teruggezet.';
        // Complete in this task: animation frames can be suspended in an
        // embedded/background browser. Waiting here leaves 48 cards with a
        // 24-card counter and lets scroll anchoring shift the viewport.
        // getBoundingClientRect below synchronously resolves the new layout.
        if (this.isConnected) {
          const card = saved.anchor?.href && Array.from(this.grid.children).find(node => node.querySelector('h3 a[href]')?.getAttribute('href') === saved.anchor.href);
          const top = card && Number.isFinite(saved.anchor.top) ? window.scrollY + card.getBoundingClientRect().top - saved.anchor.top : saved.scroll;
          window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Uw vorige plek kon niet volledig worden teruggezet. U kunt hieronder verder bladeren.';
          this.fallback.hidden = false;
        }
      } finally {
        history.scrollRestoration = previousRestoration;
        this.busy = false; this.update();
      }
    }
  }
  customElements.define('oww-load-more', LoadMore);
})();
